"use client";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import store, { RootState } from "@/redux/store";
import {
  setAuthReady,
  setTokensFromStorage,
} from "@/redux/slices/login.slice";
import useGetUser from "@/lib/user-hook";
import { clearUser } from "@/redux/slices/user.slice";
import Navbar from "./navbar/navbar";
import Footer from "./footer";
import { setIsBrowser } from "@/redux/slices/isBrowser.slice";
import { usePathname } from "next/navigation";
import AnalyticsScripts from "@/components-shared/analytics/AnalyticsScripts";
import CookieConsent from "@/components-shared/analytics/CookieConsent";
import { useFCM } from "@/lib/useFCM";
import InAppBrowserWarning from "./InAppBrowserWarning";
import { isInstagramOrFacebookInAppBrowser } from "@/lib/browserDetection";
import { ensureValidAccessToken } from "@/lib/ehjzleeInstance";
// TODO: Add translations
const MainLayout = ({ children }: { children: React.ReactNode }) => {
  useGetUser();
  const { authReady, isLoggedIn } = useSelector(
    (state: RootState) => state.login
  );
  const pathname = usePathname();
  const [showInAppWarning, setShowInAppWarning] = useState(false);
  const [error, setError] = useState<Error | undefined>();

  // Initialize FCM
  useFCM();

  useEffect(() => {
    let cancelled = false;

    const initializeAuth = async () => {
      try {
        if (typeof window !== "undefined") {
          store.dispatch(setTokensFromStorage());
          store.dispatch(setIsBrowser());

          const { isLoggedIn } = store.getState().login;
          if (isLoggedIn) {
            await ensureValidAccessToken();
          }
        }
      } catch (error) {
        console.error("Error initializing app:", error);
        setError(error as Error);
        // Show warning if in problematic browser
        if (isInstagramOrFacebookInAppBrowser()) {
          setShowInAppWarning(true);
        }
      } finally {
        if (!cancelled) {
          store.dispatch(setAuthReady(true));
        }
      }
    };

    initializeAuth();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!authReady) return;

    if (!isLoggedIn) {
      store.dispatch(clearUser());
    }
  }, [authReady, isLoggedIn]);

  return (
    <>
      {!pathname.includes("terms-and-conditions") && <Navbar />}
      {children}
      {!pathname.includes("terms-and-conditions") && <Footer />}
      <AnalyticsScripts />
      <CookieConsent />
      <InAppBrowserWarning
        show={showInAppWarning}
        onClose={() => setShowInAppWarning(false)}
        error={error}
      />
    </>
  );
};

MainLayout.displayName = "MainLayout";

export default MainLayout;
